import moment from 'jalali-moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import endpoints from '../../../config/endpoints';
import accessEnum from '../../../enums/accessEnum';
import { Sidebar } from '../../../layouts/Sidebar';
import { setLoading } from '../../../services/reducers/homeSlice';
import restApi from '../../../services/restApi';
import { useAppSelector } from '../../../services/store';
import alertHandler from '../../../utils/alertHandler';
import tools from '../../../utils/tools';
import ActivityLog from '../../../components/ActivityLog';
import Info from './Info';
import Products from './Products';
import Tapin from './Tapin';
import Access = accessEnum.Access;
import orderEnum from '../../../enums/orderEnum';
import Status = orderEnum.Status;

// a real boolean, not "0"/"1": the backend also branches on `if (isPaid)`, and
// the string "0" is truthy there
const PAID_OPTIONS = [
  { value: false, label: 'پرداخت نشده' },
  { value: true, label: 'پرداخت شده' },
];

const OrderManage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userReducer = useAppSelector(state => state.userReducer);
  const [form, setForm] = useState<any>({});
  const [allProducts, setAllProducts] = useState([]);
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [quoting, setQuoting] = useState(false);

  const selectedDeliveryMethod = deliveryMethods.find(e => e.id == form.delivery?.deliveryMethodId);
  const isTapin = selectedDeliveryMethod?.provider == 'tapin';

  // Asks Tapin what this parcel costs and drops the answer straight into the
  // shipping fee, which the invoice totals read from — the address is sent
  // along as typed, so the operator does not have to save first.
  const quoteTapinPrice = async () => {
    if (!id) return;

    setQuoting(true);

    const res = await restApi(endpoints.tapin.quote + id).post({
      deliveryMethodId: form.delivery?.deliveryMethodId,
      address: {
        ...tools.extractor(form.address || {}, ['phoneNumber', 'description', 'postalCode', 'pelak']),
        provinceId: form.address?.provinceId,
        cityId: form.address?.cityId,
      },
    });

    setQuoting(false);

    if (res.code != 200) {
      await Swal.fire({
        title: 'ناموفق',
        text: res?.data || 'محاسبه هزینه ارسال ناموفق بود',
        icon: 'error',
        confirmButtonText: 'متوجه شدم',
      });
      return;
    }

    setForm(prev => ({
      ...prev,
      delivery: { ...prev.delivery, price: res.data?.price || 0 },
    }));
  };

  const send = async () => {
    dispatch(setLoading(true));

    let paramId = id;
    let userId = form?.user?.id;
    let basicRes;
    if (!paramId) {
      basicRes = await restApi(endpoints.order.basic).post({
        phoneNumber: form?.user?.phoneNumber,
        name: form?.user?.name,
        lastName: form?.user?.lastName,
      });

      userId = basicRes.data?.user?.id;
      paramId = basicRes.data.id;
    }

    // Marking the order paid below moves it to "انتخاب مسئول خرید", which texts
    // the customer. The first address saved in the same edit would text them
    // the old status as well, so it stays quiet then: one save, one SMS.
    const movesToPickBuyer =
      !!id &&
      form.payment?.isPaid == true &&
      form.orderStatusSlug !== Status.PickBuyer &&
      tools.hasAccess(userReducer.accesses, Access.PaymentUpdate, userReducer.profile.roleId);

    // handle address
    let addressRes;
    if (id && form.address && tools.hasAccess(userReducer.accesses, Access.AddressUpdate, userReducer.profile.roleId)) {
      addressRes = await restApi(endpoints.address.basic + (form?.address?.id || '')).post({
        ...tools.extractor(form.address, ['phoneNumber', 'description', 'postalCode', 'cityId', 'provinceId']),
        userId: form.user?.id,
        // lets the backend point the order at this address — and text the
        // customer when an order that had none finally gets one
        orderId: paramId,
        notifyCustomer: !movesToPickBuyer,
      });

      // keep the id the backend just handed back: without it a second save
      // posts to the create route again and leaves a duplicate address behind -
      // and the order reads as newly addressed each time, so the customer is
      // texted about it over and over
      if (addressRes?.data?.id) {
        setForm(prev => ({ ...prev, address: { ...prev.address, id: addressRes.data.id } }));
      }
    }

    const res = await Promise.all([
      tools.hasAccess(userReducer.accesses, Access.ProductUpdate, userReducer.profile.roleId) &&
        await restApi(endpoints.order.products + paramId).put(tools.extractor(form, ['products'])),
      id && form.delivery && tools.hasAccess(userReducer.accesses, Access.DeliveryUpdate, userReducer.profile.roleId) &&
        await restApi(endpoints.order.delivery + paramId).put({
          ...tools.extractor(form.delivery, ['deliveryMethodId', 'price']),
          addressId: addressRes?.data?.id,
        }),
      // between the two: the discount is validated against the goods total the
      // products call has just recomputed, and the payment call below records
      // the payable amount, which this discount comes off
      id && await restApi(endpoints.order.discount + paramId).put({
        amount: Number(form.adminDiscount) || 0,
      }),
      id && form.payment && tools.hasAccess(userReducer.accesses, Access.PaymentUpdate, userReducer.profile.roleId) &&
        await restApi(endpoints.order.payment + paramId).put(
          tools.extractor(form.payment, ['paymentMethodId', 'code', 'isPaid']),
        ),
    ]);

    alertHandler([...res, addressRes], `سفارش با موفقیت ${id ? 'ویرایش' : 'ایجاد'} شد`, () => navigate(-1));
    dispatch(setLoading(false));
  };

  const fetchData = async () => {
    dispatch(setLoading(true));

    let res;
    if (id) {
      res = await restApi(endpoints.order.index + id, true).get();
      setForm({ ...res.data });
    }

    const result = await Promise.all([
      restApi(endpoints.product.index).get(),
      restApi(endpoints.paymentMethod.index).get(),
      restApi(endpoints.deliveryMethod.index).get(),
      restApi(endpoints.address.states).get(),
    ]);

    setAllProducts(result[0].data || []);
    setPaymentMethods(result[1].data);
    setDeliveryMethods(result[2].data);
    setStates(result[3].data || []);

    // Read the address straight off the response — `form` here is the state
    // captured when fetchData was created, so it is still empty at this point
    // and branching on it always fell through to the default province/city.
    const stateList = result[3].data || [];
    const savedAddress = res?.data?.delivery?.address;

    if (savedAddress?.cityId > 0 && savedAddress?.provinceId > 0) {
      const province = savedAddress.province || stateList.find(e => e.id == savedAddress.provinceId);
      const city = savedAddress.city || province?.cities?.find(e => e.id == savedAddress.cityId);

      setForm(prev => ({
        ...prev,
        address: {
          ...savedAddress,
          province,
          provinceId: province?.id,
          city,
          cityId: city?.id,
        },
      }));
      setCities(stateList.find(e => e.id == province?.id)?.cities || []);
    } else {
      const defaultProvince = stateList[7];
      const defaultCity = defaultProvince?.cities[11];

      setForm(prev => ({
        ...prev,
        address: {
          ...savedAddress,
          province: defaultProvince,
          provinceId: defaultProvince?.id,
          city: defaultCity,
          cityId: defaultCity?.id,
          phoneNumber: savedAddress?.phoneNumber || res?.data?.user?.phoneNumber,
        },
      }));
      setCities(defaultProvince?.cities || []);
    }

    dispatch(setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (form.address?.provinceId) {
      setCities(states.find(e => e.id == form.address?.provinceId)?.cities || []);
    }
  }, [form.address?.provinceId]);

  useEffect(() => {
    if (form.delivery?.deliveryMethodId && !form.delivery.price) {
      setForm(prev => ({
        ...prev,
        delivery: {
          ...prev.delivery,
          price: deliveryMethods.find(e => e.id == form.delivery?.deliveryMethodId)?.price,
        },
      }));
    }
  }, [form.delivery?.deliveryMethodId]);

  return (
    <>
      <body className="dashboardBody">
        <Sidebar />
        <main className="dashBoardMain main">
          <div className="addInfoHeader">
            <button className="dashboardHeader keepRight" onClick={send}>
              {id ? 'ویرایش' : 'ثبت'} سفارش
            </button>
            <span>
              <h1 className="sideBarTitle">بازگشت به صفحه لیست سفارش ها</h1>
              <h1 className="dashBoardTitle">{id ? 'ویرایش' : 'ثبت'} سفارش</h1>
            </span>
            <i className="backAdd" onClick={() => navigate('/order')}></i>
          </div>
          <Info form={form} setForm={setForm} />
          <Products
            products={form?.products || []}
            form={form}
            setForm={setForm}
            allProducts={allProducts}
          />
          <section className="orderSection">
            {id && (
              <div className="infoSection">
                <h1 className="dashBoardTitle">ارسال</h1>
                <div className="userInfoContainer">
                  <label className="sideBarTitle">روش ارسال</label>
                  <Select
                    options={tools.selectFormatter(deliveryMethods, 'id', 'title', 'انتخاب کنید')}
                    value={{ value: form.delivery?.deliveryMethodId, label: deliveryMethods.find(e => e.id == form.delivery?.deliveryMethodId)?.title }}
                    className="width100"
                    onChange={(selected) => setForm(prev => ({ ...prev, delivery: { ...prev.delivery, deliveryMethodId: selected.value } }))}
                  />
                  <label className="sideBarTitle">هزینه ارسال</label>
                  <input
                    className="persianName"
                    value={tools.formatPriceToStr(form?.delivery?.price)}
                    onChange={(input) => setForm(prev => ({ ...prev, delivery: { ...prev.delivery, price: tools.formatStrToPrice(input.target.value) } }))}
                  />
                  {isTapin && (
                    <button
                      className="dashboardHeader clickable"
                      type="button"
                      disabled={quoting}
                      onClick={quoteTapinPrice}
                    >
                      {quoting ? 'در حال محاسبه...' : 'محاسبه هزینه ارسال تاپین'}
                    </button>
                  )}
                </div>
              </div>
            )}
            {id && isTapin && (
              <Tapin
                orderId={id}
                delivery={form?.delivery}
                onChange={(delivery) => setForm(prev => ({ ...prev, delivery: delivery }))}
              />
            )}
            {id && (
              <div className="infoSection">
                <h1 className="dashBoardTitle">پرداخت</h1>
                <div className="userInfoContainer">
                  <label className="sideBarTitle">روش پرداخت</label>
                  <Select
                    options={tools.selectFormatter(paymentMethods, 'id', 'title', 'انتخاب کنید')}
                    value={{ value: form.payment?.paymentMethodId, label: paymentMethods.find(e => e.id == form.payment?.paymentMethodId)?.title }}
                    className="width100"
                    onChange={(selected) => setForm(prev => ({ ...prev, payment: { ...prev.payment, paymentMethodId: selected.value } }))}
                  />
                  <label className="sideBarTitle">شماره پیگیری</label>
                  <input className="editProductInput" value={form?.payment?.code || ''} onChange={(input) => setForm(prev => ({ ...prev, payment: { ...prev.payment, code: input.target.value } }))} />
                  <label className="sideBarTitle">وضعیت پرداخت</label>
                  <Select
                    options={PAID_OPTIONS}
                    value={PAID_OPTIONS.find(e => e.value === (form.payment?.isPaid == true))}
                    className="width100"
                    onChange={(selected) => setForm(prev => ({ ...prev, payment: { ...prev.payment, isPaid: selected.value } }))}
                  />
                </div>
              </div>
            )}
            {id && (
              <div className="infoSection">
                <h1 className="dashBoardTitle">آدرس</h1>
                <div className="userInfoContainer">
                  <label className="sideBarTitle">آدرس های پیشین</label>
                  <Select
                    className="addressInput dirRtl width100"
                    options={tools.selectFormatter(form.user?.addresses, 'id', 'description', 'انتخاب کنید')}
                    value={{ value: form.address?.id || '', label: form.user?.addresses?.find(e => e.id == form.address?.id)?.description || 'انتخاب کنید' }}
                    onChange={(selected) => setForm(prev => ({ ...prev, address: form.user?.addresses?.find(e => e.id == selected.value) }))}
                  />
                  <label className="sideBarTitle" htmlFor="province">استان</label>
                  <Select
                    options={tools.selectFormatter(states, 'id', 'title', 'انتخاب کنید')}
                    value={{ value: form.address?.province?.id, label: form.address?.province?.title }}
                    className="width100"
                    onChange={(selected) => setForm(prev => {
                      const province = states.find(e => e.id == selected.value);
                      // the first city of the new province becomes the selection.
                      // cityId has to move with it: that is the field the address
                      // is posted with, so leaving it behind saved the old
                      // province's city until the operator reselected by hand
                      const city = province?.cities?.[0];

                      return {
                        ...prev,
                        address: {
                          ...prev.address,
                          province,
                          provinceId: province?.id,
                          city,
                          cityId: city?.id,
                        },
                      };
                    })}
                  />
                  <label className="sideBarTitle" htmlFor="city">شهرستان</label>
                  <Select
                    options={tools.selectFormatter(cities, 'id', 'title', 'انتخاب کنید')}
                    value={{ value: form.address?.city?.id, label: form.address?.city?.title }}
                    className="width100"
                    onChange={(selected) => setForm(prev => {
                      const city = cities.find(e => e.id == selected.value);

                      return {
                        ...prev,
                        address: { ...prev.address, city, cityId: city?.id },
                      };
                    })}
                  />
                  <label className="sideBarTitle" htmlFor="phoneNumber">شماره تماس</label>
                  <input className="editProductInput" type="text" value={form.address?.phoneNumber || ''} onChange={(input: any) => setForm(prev => ({ ...prev, address: { ...prev.address, phoneNumber: input.target.value } }))} />
                  <label className="sideBarTitle" htmlFor="postalCode">کد پستی</label>
                  <input className="editProductInput" type="text" value={form.address?.postalCode || ''} onChange={(input: any) => setForm(prev => ({ ...prev, address: { ...prev.address, postalCode: input.target.value } }))} />
                  <label className="sideBarTitle" htmlFor="address">جزئیات آدرس</label>
                  <textarea className="editProductInput addressDescription" value={form.address?.description || ''} onChange={(input: any) => setForm(prev => ({ ...prev, address: { ...prev.address, description: input.target.value } }))} />
                </div>
              </div>
            )}
          </section>
          {id && (
            <section className="orderSection">
              <ActivityLog
                entityType="order"
                entityId={id}
                products={allProducts}
                orderProducts={form?.products || []}
              />
            </section>
          )}
        </main>
      </body>
    </>
  );
};

export default OrderManage;
